import { useState, useEffect, useCallback, useRef, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { MoreVertical } from 'lucide-react';
import type { Scenario, UpdateScenarioInput } from '../../../models/simulation';
import { confirmationService } from '../../../services/confirmationService';

interface ScenarioTabProps {
    scenario: Scenario;
    isActive: boolean;
    isRunning?: boolean;
    progress?: { completedTrials: number; totalTrials: number };
    onSelect: () => void;
    onUpdate?: (scenarioId: string, input: UpdateScenarioInput) => Promise<void>;
    onDelete?: (scenario: Scenario) => Promise<void>;
}

export const ScenarioTab = ({ scenario, isActive, isRunning = false, progress, onSelect, onUpdate, onDelete }: ScenarioTabProps) => {
    const [modalOpen, setModalOpen] = useState(false);
    const [name, setName] = useState(scenario.name);
    const [description, setDescription] = useState(scenario.description || '');
    const [isSaving, setIsSaving] = useState(false);
    const dialogRef = useRef<HTMLDivElement | null>(null);
    const triggerRef = useRef<HTMLButtonElement | null>(null);

    // Update local state when scenario changes
    useEffect(() => {
        setName(scenario.name);
        setDescription(scenario.description || '');
    }, [scenario]);

    // Handle escape key to close modal
    useEffect(() => {
        if (!modalOpen) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                setModalOpen(false);
                // Reset to original values
                setName(scenario.name);
                setDescription(scenario.description || '');
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [modalOpen, scenario]);

    // Keep keyboard focus inside the modal while open.
    useEffect(() => {
        if (!modalOpen || !dialogRef.current) return;
        const selector = 'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
        const focusableElements = dialogRef.current.querySelectorAll<HTMLElement>(selector);
        focusableElements[0]?.focus();
    }, [modalOpen]);

    const handleOpenModal = useCallback(() => {
        setName(scenario.name);
        setDescription(scenario.description || '');
        setModalOpen(true);
    }, [scenario]);

    const handleSave = useCallback(async () => {
        if (!onUpdate || !name.trim()) return;

        setIsSaving(true);
        try {
            await onUpdate(scenario.id, {
                name: name.trim(),
                description: description.trim() || undefined,
            });
            setModalOpen(false);
            triggerRef.current?.focus();
        } catch (error) {
            console.error('Failed to update scenario:', error);
        } finally {
            setIsSaving(false);
        }
    }, [onUpdate, scenario.id, name, description]);

    const handleDelete = useCallback(async () => {
        if (!onDelete) return;

        const confirmed = await confirmationService.confirm(
            `Are you sure you want to delete the scenario "${scenario.name}"? This action cannot be undone.`,
            {
                confirmText: 'Delete',
                cancelText: 'Cancel',
                isDestructive: true,
            }
        );

        if (confirmed) {
            await onDelete(scenario);
            setModalOpen(false);
            triggerRef.current?.focus();
        }
    }, [onDelete, scenario]);

    const handleCancel = useCallback(() => {
        setModalOpen(false);
        // Reset to original values
        setName(scenario.name);
        setDescription(scenario.description || '');
        triggerRef.current?.focus();
    }, [scenario]);

    const handleDialogKeyDown = useCallback((e: ReactKeyboardEvent<HTMLDivElement>) => {
        if (!dialogRef.current) return;
        if (e.key === 'Enter' && e.target instanceof HTMLTextAreaElement) return;

        if (e.key === 'Enter') {
            if (name.trim() && !isSaving) {
                e.preventDefault();
                void handleSave();
            }
            return;
        }

        if (e.key !== 'Tab') return;
        const selector = 'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
        const focusableElements = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(selector));
        if (focusableElements.length === 0) return;

        const first = focusableElements[0];
        const last = focusableElements[focusableElements.length - 1];
        const active = document.activeElement as HTMLElement | null;
        if (!active) return;

        if (e.shiftKey && active === first) {
            e.preventDefault();
            last.focus();
            return;
        }

        if (!e.shiftKey && active === last) {
            e.preventDefault();
            first.focus();
        }
    }, [handleSave, isSaving, name]);

    const hasMenu = Boolean(onUpdate || onDelete);
    const trimmedName = name.trim();
    const showNameError = trimmedName.length === 0;
    const saveDisabled = showNameError || isSaving;
    const titleId = `edit-scenario-title-${scenario.id}`;
    const subtitleId = `edit-scenario-subtitle-${scenario.id}`;
    const nameErrorId = `edit-scenario-name-error-${scenario.id}`;

    const tooltipContent = scenario.isBaseline
        ? 'Baseline Scenario always uses the default distributions from the graph. To test different values, create a new scenario.'
        : scenario.description || null;

    return (
        <>
            <div className={`scenario-tab ${hasMenu ? 'has-menu' : ''} ${isActive ? 'active' : ''} ${isRunning ? 'running' : ''}`}>
                <button
                    type="button"
                    className="scenario-tab-button"
                    onClick={onSelect}
                >
                    <span className="scenario-tab-name">{scenario.name}</span>
                    {isRunning && progress && (
                        <span className="scenario-tab-progress">
                            {progress.completedTrials.toLocaleString()}/{progress.totalTrials.toLocaleString()}
                            {' '}
                            ({Math.round((progress.completedTrials / progress.totalTrials) * 100)}%)
                        </span>
                    )}
                </button>
                {tooltipContent && (
                    <div className="scenario-tab-tooltip">
                        {tooltipContent}
                    </div>
                )}
                {hasMenu && (
                    <button
                        type="button"
                        className="scenario-tab-menu-trigger"
                        onClick={handleOpenModal}
                        aria-label={`Edit scenario ${scenario.name}`}
                        ref={triggerRef}
                    >
                        <MoreVertical size={16} />
                    </button>
                )}
            </div>

            {/* Edit Scenario Modal */}
            {modalOpen && (
                <div className="scenario-create-dialog-overlay" onClick={handleCancel}>
                    <div
                        className="scenario-create-dialog scenario-edit-dialog"
                        onClick={(e) => e.stopPropagation()}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby={titleId}
                        aria-describedby={subtitleId}
                        ref={dialogRef}
                        onKeyDown={handleDialogKeyDown}
                    >
                        <h3 id={titleId}>Edit Scenario</h3>
                        <p id={subtitleId} className="scenario-edit-subtitle">Update the scenario name and optional description.</p>
                        <div className="scenario-create-form">
                            <div className="scenario-create-field">
                                <label>Name *</label>
                                <input
                                    type="text"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="Scenario name"
                                    autoFocus
                                    required
                                    aria-invalid={showNameError}
                                    aria-describedby={showNameError ? nameErrorId : undefined}
                                />
                                {showNameError && (
                                    <p id={nameErrorId} className="scenario-edit-validation-message" role="alert">
                                        Name is required.
                                    </p>
                                )}
                            </div>
                            <div className="scenario-create-field">
                                <label>Description</label>
                                <textarea
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    placeholder="Optional description"
                                    rows={3}
                                />
                                <p className="scenario-edit-helper-text">Optional context shown in scenario tooltips.</p>
                            </div>
                            <div className="scenario-create-actions">
                                <button
                                    type="button"
                                    className="btn-secondary"
                                    onClick={handleCancel}
                                    disabled={isSaving}
                                >
                                    Cancel
                                </button>
                                {onUpdate && (
                                    <button
                                        type="button"
                                        className="btn-primary"
                                        onClick={handleSave}
                                    disabled={saveDisabled}
                                    >
                                        {isSaving ? 'Saving...' : 'Save'}
                                    </button>
                                )}
                            </div>
                            {onDelete && (
                                <div className="scenario-edit-danger-zone">
                                    <p className="scenario-edit-danger-zone-title">Danger zone</p>
                                    <button
                                        type="button"
                                        className="btn-danger scenario-edit-delete-btn"
                                        onClick={handleDelete}
                                        disabled={isSaving}
                                    >
                                        Delete scenario
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

